
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PhoneVerificationDialog } from "./phone-verification/PhoneVerificationDialog";
import { PersonalInfoFields } from "./profile/PersonalInfoFields";
import { PhoneNumberField } from "./profile/PhoneNumberField";

interface Profile {
  full_name: string;
  email: string;
  bio: string;
  phone_number?: string;
  phone_verified?: boolean;
}

export const ProfileInfoForm = ({
  profile,
  loading,
  onProfileReload,
}: {
  profile: Profile;
  loading: boolean;
  onProfileReload: () => void;
}) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [localProfile, setLocalProfile] = useState<Profile>(profile);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === "phone_number" && value !== profile.phone_number) {
      setLocalProfile(prev => ({
        ...prev,
        [name]: value,
        phone_verified: false
      }));
    } else {
      setLocalProfile(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);

    const { data: userResult } = await supabase.auth.getUser();
    const userId = userResult?.user?.id;

    if (!userId) {
      toast({
        variant: "destructive",
        title: "Authentication error",
        description: "Unable to identify the current user.",
      });
      setSaving(false);
      return;
    }

    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: localProfile.full_name,
        bio: localProfile.bio,
        phone_number: localProfile.phone_number || null,
        phone_verified: localProfile.phone_verified || false,
      })
      .eq("id", userId);

    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to update profile",
        description: error.message,
      });
    } else {
      toast({
        title: "Profile updated",
        description: "Your profile has been updated.",
      });
      onProfileReload();
    }
    setSaving(false);
  }

  const handleVerificationComplete = () => {
    setLocalProfile(prev => ({
      ...prev,
      phone_verified: true
    }));
    onProfileReload();
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>Update your personal details</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSaveProfile}>
            <PersonalInfoFields
              fullName={localProfile.full_name}
              email={localProfile.email}
              bio={localProfile.bio}
              loading={loading}
              onChange={handleChange}
            />
            
            <PhoneNumberField
              phoneNumber={localProfile.phone_number || ''}
              isVerified={localProfile.phone_verified || false}
              loading={loading}
              onChange={handleChange}
              onVerificationRequest={() => setVerificationDialogOpen(true)}
            />

            <Button 
              className="bg-ai-indigo hover:bg-ai-indigo/90" 
              type="submit" 
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <PhoneVerificationDialog
        phoneNumber={localProfile.phone_number || ''}
        isOpen={verificationDialogOpen}
        onClose={() => setVerificationDialogOpen(false)}
        onVerified={handleVerificationComplete}
      />
    </>
  );
};
